import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Tenant } from '@/types/database.types'
import { getCachedTenant, setCachedTenant } from '@/lib/tenant-cache'

interface TenantContextType {
  tenant: Tenant | null
  loading: boolean
  error: string | null
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

interface TenantProviderProps {
  slug?: string
  children: ReactNode
}

export function TenantProvider({ slug: slugProp, children }: TenantProviderProps) {
  const params = useParams<{ slug: string }>()
  const slug = slugProp || params.slug || ''
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) {
      setLoading(false)
      setError('No tenant slug provided')
      return
    }

    const resolve = async () => {
      // Check cache first
      const cached = getCachedTenant(slug)
      if (cached) {
        setTenant(cached)
        setLoading(false)
        return
      }

      try {
        const { data, error: dbError } = await supabase
          .from('tenants')
          .select('*')
          .eq('slug', slug)
          .in('status', ['active', 'trial'])
          .single()

        if (dbError || !data) {
          setError(`Tenant "${slug}" not found`)
          setTenant(null)
        } else {
          setCachedTenant(slug, data)
          setTenant(data)
          setError(null)
        }
      } catch {
        setError('Failed to resolve tenant')
      } finally {
        setLoading(false)
      }
    }

    resolve()
  }, [slug])

  return (
    <TenantContext.Provider value={{ tenant, loading, error }}>
      {children}
    </TenantContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTenant() {
  const context = useContext(TenantContext)
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider')
  }
  return context
}
